import React, { useEffect, useState } from 'react';
import TodayTab from './tabs/TodayTab';
import CurrentYearTab from './tabs/CurrentYearTab';
import TenYearAverageTab from './tabs/TenYearAverageTab';
import ToDateTab from './tabs/ToDateTab';
import HistoricalTab from './tabs/HistoricalTab';
import { fetchWildfireStats, fetchTodayStats, fetchHistoricalStats } from './statsService';
import type { WildfireStatsResponse, TodayStatsResponse, HistoricalComparison } from './statsTypes';
import './WildfireStatsDashboard.css';

type TabKey = 'today' | 'currentYear' | 'tenYearAverage' | 'toDate' | 'historical';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'currentYear', label: 'Current Year' },
  { key: 'tenYearAverage', label: '10-Year Average' },
  { key: 'toDate', label: '10-Yr To-Date' },
  { key: 'historical', label: 'Historical' },
];

const WildfireStatsDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('today');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wildfireStats, setWildfireStats] = useState<WildfireStatsResponse | null>(null);
  const [todayStats, setTodayStats] = useState<TodayStatsResponse | null>(null);
  const [historicalStats, setHistoricalStats] = useState<HistoricalComparison[] | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      try {
        setLoading(true);
        setError(null);
        const [wildfire, today, historical] = await Promise.all([
          fetchWildfireStats(),
          fetchTodayStats(),
          fetchHistoricalStats(),
        ]);
        if (!cancelled) {
          setWildfireStats(wildfire);
          setTodayStats(today);
          setHistoricalStats(historical);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load statistics');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadData();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="wildfire-stats-dashboard">
      <h2 className="wildfire-stats-dashboard__title">Wildfire Statistics</h2>
      <div className="wildfire-stats-dashboard__tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`wildfire-stats-dashboard__tab ${activeTab === tab.key ? 'wildfire-stats-dashboard__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="wildfire-stats-dashboard__content">
        {loading && <div className="wildfire-stats-dashboard__loading">Loading statistics...</div>}
        {error && <div className="wildfire-stats-dashboard__error">Error: {error}</div>}
        {!loading && !error && (
          <>
            {activeTab === 'today' && <TodayTab data={todayStats} />}
            {activeTab === 'currentYear' && <CurrentYearTab data={wildfireStats?.currentYear ?? null} />}
            {activeTab === 'tenYearAverage' && <TenYearAverageTab data={wildfireStats?.tenYearAverage ?? null} />}
            {activeTab === 'toDate' && <ToDateTab data={wildfireStats?.tenYearAverageToDate ?? null} dayOfYear={wildfireStats?.dayOfYear} />}
            {activeTab === 'historical' && <HistoricalTab data={wildfireStats?.historicalYears ?? []} />}
          </>
        )}
      </div>
    </div>
  );
};

export default WildfireStatsDashboard;
